import {
  Baby,
  Car,
  Coffee,
  PawPrint,
  Snowflake,
  Tv,
  UtensilsCrossed,
  Waves,
  Wifi,
  type LucideIcon,
} from 'lucide-react'
import type { AppLang } from '@/lib/format'

export interface AmenityDef {
  code: string
  icon: LucideIcon
  label: Record<AppLang, string>
}

// Fixed vocabulary (not free text) so the frontend can show a consistent
// icon + label everywhere a room type appears. `LodgingRoomType.amenities`
// stores a comma-separated subset of these codes.
export const AMENITIES: AmenityDef[] = [
  { code: 'wifi', icon: Wifi, label: { uk: 'Wi-Fi', en: 'Wi-Fi', ru: 'Wi-Fi' } },
  {
    code: 'ac',
    icon: Snowflake,
    label: { uk: 'Кондиціонер', en: 'Air conditioning', ru: 'Кондиционер' },
  },
  { code: 'tv', icon: Tv, label: { uk: 'Телевізор', en: 'TV', ru: 'Телевизор' } },
  {
    code: 'kitchen',
    icon: UtensilsCrossed,
    label: { uk: 'Кухня', en: 'Kitchen', ru: 'Кухня' },
  },
  { code: 'parking', icon: Car, label: { uk: 'Парковка', en: 'Parking', ru: 'Парковка' } },
  {
    code: 'crib',
    icon: Baby,
    label: { uk: 'Дитяче ліжечко', en: 'Baby crib', ru: 'Детская кроватка' },
  },
  {
    code: 'breakfast',
    icon: Coffee,
    label: { uk: 'Сніданок', en: 'Breakfast', ru: 'Завтрак' },
  },
  {
    code: 'pets',
    icon: PawPrint,
    label: { uk: 'Можна з тваринами', en: 'Pets allowed', ru: 'Можно с животными' },
  },
  {
    code: 'pool',
    icon: Waves,
    label: { uk: 'Басейн з підігрівом', en: 'Heated pool', ru: 'Бассейн с подогревом' },
  },
  {
    code: 'sea',
    icon: Waves,
    label: { uk: '550м до моря (8–10 хв)', en: '550m to beach (8–10 min)', ru: '550м до моря (8–10 мин)' },
  },
  {
    code: 'fridge',
    icon: UtensilsCrossed,
    label: { uk: 'Холодильник', en: 'Refrigerator', ru: 'Холодильник' },
  },
  {
    code: 'hairdryer',
    icon: Snowflake,
    label: { uk: 'Фен', en: 'Hairdryer', ru: 'Фен' },
  },
  {
    code: 'balcony',
    icon: Coffee,
    label: { uk: 'Балкон', en: 'Balcony', ru: 'Балкон' },
  },
  {
    code: 'barbecue',
    icon: UtensilsCrossed,
    label: { uk: 'Мангальна зона', en: 'BBQ area', ru: 'Мангальная зона' },
  },
]

const BY_CODE = new Map(AMENITIES.map((a) => [a.code, a]))

export function parseAmenities(amenities: string | undefined | null): AmenityDef[] {
  if (!amenities) return []
  const codes = amenities
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean)
  // Deduplicate: the field is admin-edited free text, and consumers key
  // the rendered chips by `code` — a repeat would mean duplicate React
  // keys and the same amenity shown twice.
  return [...new Set(codes)]
    .map((code) => BY_CODE.get(code))
    .filter((a): a is AmenityDef => Boolean(a))
}
